import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Loader2, CheckCircle2, Phone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { fileUrl } from "@/lib/products";

const LeadForm = ({ site, slug, cta }) => {
  const [f, setF] = useState({ name: "", phone: "", email: "", message: "", website: "" });
  const [state, setState] = useState("idle");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setState("busy"); setErr("");
    try { await api.post(`/api/lp/${site}/${slug}/lead`, f); setState("done"); }
    catch (ex) { const d = ex.response?.data?.detail; setErr(Array.isArray(d) ? d.map(x => x.msg).join(", ") : d || "Something went wrong"); setState("idle"); }
  };
  if (state === "done") return <div className="flex items-center gap-2 text-green-700 font-semibold" data-testid="lp-lead-success"><CheckCircle2 className="w-5 h-5" />Thanks! We'll be in touch shortly.</div>;
  return (
    <form onSubmit={submit} className="space-y-2.5" data-testid="lp-lead-form">
      <Input required placeholder="Your name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} className="bg-white" data-testid="lp-lead-name" />
      <Input placeholder="Phone" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} className="bg-white" data-testid="lp-lead-phone" />
      <Input type="email" placeholder="Email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} className="bg-white" data-testid="lp-lead-email" />
      <Textarea rows={3} placeholder="How can we help?" value={f.message} onChange={e => setF({ ...f, message: e.target.value })} className="bg-white" data-testid="lp-lead-message" />
      <input tabIndex={-1} autoComplete="off" className="hidden" value={f.website} onChange={e => setF({ ...f, website: e.target.value })} />
      {err && <p className="text-sm text-red-600" data-testid="lp-lead-error">{err}</p>}
      <button disabled={state === "busy"} className="w-full bg-orange-500 hover:bg-orange-400 text-white font-bold py-3 rounded-lg disabled:opacity-50" data-testid="lp-lead-submit">{state === "busy" ? "Sending…" : cta || "Request a free quote"}</button>
    </form>
  );
};

export default function HostedLandingPage() {
  const { site, slug } = useParams();
  const [d, setD] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    api.get(`/api/lp/${site}/${slug}`).then(r => {
      setD(r.data);
      document.title = r.data.page.meta_title || r.data.page.title;
      let m = document.querySelector('meta[name="description"]');
      if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
      m.content = r.data.page.meta_description || "";
    }).catch(() => setMissing(true));
  }, [site, slug]);
  if (missing) return <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="lp-missing">This page isn't available.</div>;
  if (!d) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  const c = d.page.content;
  return (
    <div className="min-h-screen bg-white" data-testid="hosted-landing-page">
      <header className="border-b border-slate-100 px-6 py-4 flex items-center justify-between max-w-6xl mx-auto">
        <div className="flex items-center gap-3">{d.logo_url && <img src={fileUrl(d.logo_url)} alt="" className="h-10 w-auto" />}<span className="font-black text-slate-900">{d.business_name}</span></div>
        {d.phone && <a href={`tel:${d.phone}`} className="flex items-center gap-1.5 text-sm font-bold text-orange-600"><Phone className="w-4 h-4" />{d.phone}</a>}
      </header>
      <section className="bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-5 gap-10 items-start">
          <div className="md:col-span-3">
            {d.location && <p className="text-orange-400 text-xs font-bold uppercase tracking-widest mb-3">{d.location}</p>}
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-4" data-testid="lp-h1">{c.h1 || d.page.title}</h1>
            <p className="text-slate-300 text-lg">{c.intro}</p>
          </div>
          <div className="md:col-span-2 bg-white/5 border border-white/10 rounded-2xl p-5"><p className="font-bold mb-3">Get in touch</p><LeadForm site={site} slug={slug} cta={c.cta} /></div>
        </div>
      </section>
      <main className="max-w-3xl mx-auto px-6 py-14 space-y-10">
        {c.sections.map((s, i) => <section key={i}><h2 className="text-2xl font-bold text-slate-900 mb-2">{s.heading}</h2><p className="text-slate-600 leading-relaxed">{s.body}</p></section>)}
        {c.faqs.length > 0 && (
          <section><h2 className="text-2xl font-bold text-slate-900 mb-4">Frequently asked questions</h2>
            <div className="space-y-4">{c.faqs.map((f, i) => <div key={i} className="border-b border-slate-100 pb-4"><p className="font-semibold text-slate-900">{f.q}</p><p className="text-slate-600 mt-1">{f.a}</p></div>)}</div>
          </section>
        )}
      </main>
      <footer className="text-center text-xs text-slate-400 py-8 border-t border-slate-100">© {new Date().getFullYear()} {d.business_name}</footer>
    </div>
  );
}
